import { Injectable, signal, computed, inject, OnDestroy } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { tap, map, takeUntil } from 'rxjs/operators';
import {
    Notification,
    NotificationStats,
    NotificationType,
    NotificationCategory,
    NotificationPreferences,
    SecurityEvent,
    NotificationQueryParams,
    UpdatePreferencesDto,
} from '@/app/shared/interfaces/notification.interface';
import { NotificationApiService } from './notification-api.service';
import { NotificationWebSocketService } from './notification-websocket.service';
import { ResponsePaginatedWrapper } from '@/app/shared/dto';

/**
 * Service for managing notification state and real-time updates
 * Uses Angular signals for reactive state management
 * Fetches data from backend via NotificationApiService
 * Receives real-time updates via NotificationWebSocketService
 */
@Injectable({ providedIn: 'root' })
export class NotificationsService implements OnDestroy {
    private apiService = inject(NotificationApiService);
    private wsService = inject(NotificationWebSocketService);
    private destroy$ = new Subject<void>();

    // Signal-based state management
    private notificationsSignal = signal<Notification[]>([]);
    private unreadCountSignal = signal<number>(0); // Actual server count, not computed
    private statsSignal = signal<NotificationStats | null>(null);
    private preferencesSignal = signal<NotificationPreferences | null>(null);

    // Computed values
    notifications = this.notificationsSignal.asReadonly();
    unreadCount = this.unreadCountSignal.asReadonly(); // Use server count for accuracy
    unreadNotifications = computed(() => this.notificationsSignal().filter((n: Notification) => !n.isRead));
    stats = this.statsSignal.asReadonly();
    preferences = this.preferencesSignal.asReadonly();
    wsConnected = this.wsService.connected;

    // Audio for notification sounds
    private notificationAudio: HTMLAudioElement | null = null;

    constructor() {
        this.setupWebSocketListeners();
        this.initializeNotificationSound();
        this.loadPreferences().subscribe(); // Load preferences on initialization
        // Fetch unread count on app boot so the sidebar badge is correct even
        // before the user opens the bell overlay.
        this.loadStats().subscribe({ error: () => {} });
    }

    /**
     * Initialize the notification sound audio element
     * Uses a professional notification sound from assets
     */
    private initializeNotificationSound(): void {
        // Create audio element for notification sound
        this.notificationAudio = new Audio();
        // Use professional notification sound from assets
        this.notificationAudio.src = '/assets/sounds/new-notification.mp3';
        this.notificationAudio.volume = 0.6; // Set volume to 60% for good noticeability
        this.notificationAudio.preload = 'auto'; // Preload for instant playback
    }

    /**
     * Play notification sound if enabled in user preferences
     */
    private playNotificationSound(): void {
        const prefs = this.preferencesSignal();

        // Only play sound if preferences are loaded and sound is enabled
        if (prefs && prefs.soundEnabled && this.notificationAudio) {
            this.notificationAudio.currentTime = 0; // Reset to start
            this.notificationAudio.play().catch((error) => {
                console.warn('[NotificationsService] Could not play notification sound:', error);
            });
        }
    }

    /**
     * Setup WebSocket event listeners for real-time updates
     */
    private setupWebSocketListeners(): void {
        // New notification received
        this.wsService.onNotificationReceived.pipe(takeUntil(this.destroy$)).subscribe((notification: Notification) => {
            console.log('[NotificationsService] New notification from WebSocket:', notification);
            this.addNotification(notification);
            // Play notification sound if enabled
            this.playNotificationSound();
        });

        // Notification updated
        this.wsService.onNotificationUpdated.pipe(takeUntil(this.destroy$)).subscribe((notification: Notification) => {
            console.log('[NotificationsService] Notification updated from WebSocket:', notification);
            this.updateNotification(notification);
        });

        // Notification deleted
        this.wsService.onNotificationDeleted.pipe(takeUntil(this.destroy$)).subscribe((notificationId: string) => {
            console.log('[NotificationsService] Notification deleted from WebSocket:', notificationId);
            this.removeNotification(notificationId);
        });

        // Unread count updated (from server via WebSocket)
        this.wsService.onUnreadCountUpdated.pipe(takeUntil(this.destroy$)).subscribe((count: number) => {
            console.log('[NotificationsService] Unread count updated from WebSocket:', count);
            this.unreadCountSignal.set(count);
        });

        // Stats updated
        this.wsService.onStatsUpdated.pipe(takeUntil(this.destroy$)).subscribe((stats: NotificationStats) => {
            console.log('[NotificationsService] Stats updated from WebSocket:', stats);
            this.statsSignal.set(stats);
        });

        // Handle errors
        this.wsService.onError.pipe(takeUntil(this.destroy$)).subscribe((error: string) => {
            console.error('[NotificationsService] WebSocket error:', error);
        });
    }

    /**
     * Load all notifications for current user
     */
    loadNotifications(params?: NotificationQueryParams): Observable<Notification[]> {
        return this.apiService.getNotifications(params).pipe(
            tap((response: ResponsePaginatedWrapper<Notification>) => {
                this.notificationsSignal.set(response.data);
            }),
            map((response: ResponsePaginatedWrapper<Notification>) => response.data)
        );
    }

    /**
     * Get all notifications (returns cached if available, otherwise loads)
     */
    getNotifications(): Observable<Notification[]> {
        const current = this.notificationsSignal();
        if (current.length > 0) {
            return new Observable((observer) => {
                observer.next(current);
                observer.complete();
            });
        }
        return this.loadNotifications();
    }

    /**
     * Get unread notifications
     */
    getUnreadNotifications(): Observable<Notification[]> {
        return this.apiService.getNotifications({ isRead: false }).pipe(
            tap((response: ResponsePaginatedWrapper<Notification>) => {
                // Update signal with all notifications, keeping existing read ones
                const unread = response.data;
                const read = this.notificationsSignal().filter((n: Notification) => n.isRead);
                this.notificationsSignal.set([...unread, ...read]);
            }),
            map((response: ResponsePaginatedWrapper<Notification>) => response.data)
        );
    }

    /**
     * Load notification statistics
     */
    loadStats(): Observable<NotificationStats> {
        return this.apiService.getStats().pipe(
            tap((stats: NotificationStats) => {
                this.statsSignal.set(stats);
                // Sync unread count from stats
                this.unreadCountSignal.set(stats.unread);
            })
        );
    }

    /**
     * Get notification statistics (cached or fresh)
     */
    getStats(): Observable<NotificationStats> {
        const current = this.statsSignal();
        if (current) {
            return new Observable((observer) => {
                observer.next(current);
                observer.complete();
            });
        }
        return this.loadStats();
    }

    /**
     * Mark notification as read
     */
    markAsRead(notificationId: string): Observable<Notification> {
        return this.apiService.markAsRead(notificationId).pipe(
            tap((updated) => {
                // Update signal
                const notifications = this.notificationsSignal();
                this.notificationsSignal.set(
                    notifications.map((n: Notification) => (n.id === notificationId ? updated : n))
                );
            })
        );
    }

    /**
     * Mark all notifications as read
     */
    markAllAsRead(): Observable<void> {
        return this.apiService.markAllAsRead().pipe(
            tap(() => {
                // Update signal
                const notifications = this.notificationsSignal();
                const now = new Date();
                this.notificationsSignal.set(
                    notifications.map((n: Notification) => ({
                        ...n,
                        isRead: true,
                        readAt: n.readAt || now,
                    }))
                );
            })
        );
    }

    /**
     * Delete notification
     */
    deleteNotification(notificationId: string): Observable<void> {
        return this.apiService.deleteNotification(notificationId).pipe(
            tap(() => {
                // Update signal
                const notifications = this.notificationsSignal();
                this.notificationsSignal.set(notifications.filter((n: Notification) => n.id !== notificationId));
            })
        );
    }

    /**
     * Delete all notifications (optionally filtered by archived status)
     */
    deleteAllNotifications(isArchived?: string): Observable<void> {
        return this.apiService.deleteAllNotifications(isArchived).pipe(
            tap(() => {
                this.notificationsSignal.set([]);
                this.unreadCountSignal.set(0);
                this.statsSignal.set(null);
            })
        );
    }

    /**
     * Archive a notification
     */
    archiveNotification(notificationId: string): Observable<void> {
        return this.apiService.archiveNotification(notificationId).pipe(
            tap(() => {
                this.notificationsSignal.update((notifications: Notification[]) =>
                    notifications.filter((n) => n.id !== notificationId)
                );
            })
        );
    }

    /**
     * Archive all notifications
     */
    archiveAllNotifications(): Observable<void> {
        return this.apiService.archiveAllNotifications().pipe(
            tap(() => {
                this.notificationsSignal.set([]);
                this.unreadCountSignal.set(0);
                this.statsSignal.set(null);
            })
        );
    }

    /**
     * Get notifications by category
     */
    getNotificationsByCategory(category: NotificationCategory): Observable<Notification[]> {
        return this.apiService.getByCategory(category);
    }

    /**
     * Load user notification preferences
     */
    loadPreferences(): Observable<NotificationPreferences> {
        return this.apiService.getPreferences().pipe(
            tap((prefs) => {
                this.preferencesSignal.set(prefs);
            })
        );
    }

    /**
     * Get user notification preferences (cached or fresh)
     */
    getPreferences(): Observable<NotificationPreferences> {
        const current = this.preferencesSignal();
        if (current) {
            return new Observable((observer) => {
                observer.next(current);
                observer.complete();
            });
        }
        return this.loadPreferences();
    }

    /**
     * Update user notification preferences
     */
    updatePreferences(preferences: UpdatePreferencesDto): Observable<NotificationPreferences> {
        return this.apiService.updatePreferences(preferences).pipe(
            tap((updated) => {
                this.preferencesSignal.set(updated);
            })
        );
    }

    /**
     * Get security events (for admin dashboard) with filtering and pagination
     */
    getSecurityEvents(params?: {
        period?: '1d' | '7d' | '30d' | 'all';
        page?: number;
        limit?: number;
    }): Observable<{ data: SecurityEvent[]; meta: any }> {
        return this.apiService.getSecurityEvents(params);
    }

    /**
     * Search notifications
     */
    searchNotifications(query: string): Observable<Notification[]> {
        return this.apiService.searchNotifications(query);
    }

    /**
     * Get a specific notification by ID
     */
    getNotification(id: string): Observable<Notification> {
        return this.apiService.getNotification(id);
    }

    /**
     * Add a new notification to the local state (for real-time updates via Socket.IO)
     * This is called when a new notification is received via WebSocket
     * Note: The server sends updated unread_count via WebSocket automatically
     */
    addNotification(notification: Notification): void {
        this.notificationsSignal.update((notifications: Notification[]) => [notification, ...notifications]);

        // Update total count in stats
        const currentStats = this.statsSignal();
        if (currentStats) {
            this.statsSignal.set({
                ...currentStats,
                total: currentStats.total + 1,
                // Don't update unread here - server sends accurate count via WebSocket
            });
        }
    }

    /**
     * Update a notification in the local state (for real-time updates via Socket.IO)
     */
    updateNotification(notification: Notification): void {
        this.notificationsSignal.update((notifications: Notification[]) =>
            notifications.map((n) => (n.id === notification.id ? notification : n))
        );
    }

    /**
     * Remove a notification from the local state (for real-time updates via Socket.IO)
     */
    removeNotification(notificationId: string): void {
        this.notificationsSignal.update((notifications: Notification[]) =>
            notifications.filter((n) => n.id !== notificationId)
        );
    }

    /**
     * Refresh all data (notifications, stats, preferences)
     */
    refresh(): void {
        this.loadNotifications().subscribe();
        this.loadStats().subscribe();
        this.loadPreferences().subscribe();
    }

    /**
     * Clear all cached data
     */
    clear(): void {
        this.notificationsSignal.set([]);
        this.statsSignal.set(null);
        this.preferencesSignal.set(null);
    }

    /**
     * Helper to get type-to-category mapping
     */
    getCategoryForType(type: NotificationType): NotificationCategory {
        const typeToCategory: Record<string, NotificationCategory> = {
            // Security
            DEVICE_AUTHENTICATION_ALERT: NotificationCategory.SYSTEM_SECURITY,
            LOGIN_ACTIVITY: NotificationCategory.SYSTEM_SECURITY,
            LOGIN_SUCCESS: NotificationCategory.SYSTEM_SECURITY,
            FAILED_LOGIN_ATTEMPT: NotificationCategory.SYSTEM_SECURITY,
            MULTIPLE_FAILED_LOGINS: NotificationCategory.SYSTEM_SECURITY,
            PASSWORD_CHANGED: NotificationCategory.SYSTEM_SECURITY,
            PASSWORD_RESET_REQUESTED: NotificationCategory.SYSTEM_SECURITY,
            PERMISSION_CHANGE: NotificationCategory.SYSTEM_SECURITY,
            ACCOUNT_LOCKOUT: NotificationCategory.SYSTEM_SECURITY,
            USER_ROLE_MODIFIED: NotificationCategory.SYSTEM_SECURITY,
            SYSTEM_CONFIG_CHANGE: NotificationCategory.SYSTEM_SECURITY,
            SUSPICIOUS_ACTIVITY_DETECTED: NotificationCategory.SYSTEM_SECURITY,
            NEW_DEVICE_LOGIN: NotificationCategory.SYSTEM_SECURITY,

            // Project
            PROJECT_CREATED: NotificationCategory.PROJECT_LIFECYCLE,
            DRAFT_PROJECT_CREATED: NotificationCategory.PROJECT_LIFECYCLE,
            DRAFT_PROJECT_APPROVAL_REQUEST: NotificationCategory.PROJECT_LIFECYCLE,
            PROJECT_APPROVED: NotificationCategory.PROJECT_LIFECYCLE,
            PROJECT_REJECTED: NotificationCategory.PROJECT_LIFECYCLE,
            PROJECT_STATUS_CHANGED: NotificationCategory.PROJECT_LIFECYCLE,
            PROJECT_TIMELINE_UPDATED: NotificationCategory.PROJECT_LIFECYCLE,
            PROJECT_DEADLINE_APPROACHING_48H: NotificationCategory.PROJECT_LIFECYCLE,
            PROJECT_DEADLINE_APPROACHING_24H: NotificationCategory.PROJECT_LIFECYCLE,
            PROJECT_DEADLINE_APPROACHING_8H: NotificationCategory.PROJECT_LIFECYCLE,
            PROJECT_OVERDUE: NotificationCategory.PROJECT_LIFECYCLE,
            PROJECT_COMPLETED: NotificationCategory.PROJECT_LIFECYCLE,
            PROJECT_ASSIGNED: NotificationCategory.PROJECT_LIFECYCLE,

            // Tasks
            TASK_ASSIGNED: NotificationCategory.TASK_MANAGEMENT,
            TASK_REASSIGNED: NotificationCategory.TASK_MANAGEMENT,
            TASK_PRIORITY_CHANGED: NotificationCategory.TASK_MANAGEMENT,
            TASK_DUE_DATE_MODIFIED: NotificationCategory.TASK_MANAGEMENT,
            TASK_STATUS_UPDATED: NotificationCategory.TASK_MANAGEMENT,
            TASK_COMPLETED: NotificationCategory.TASK_MANAGEMENT,
            TASK_COMMENT_ADDED: NotificationCategory.TASK_MANAGEMENT,
            TASK_OVERDUE: NotificationCategory.TASK_MANAGEMENT,

            // Documents
            DOCUMENT_UPLOADED: NotificationCategory.DOCUMENT_WORKFLOW,
            DOCUMENT_PROCESSING_COMPLETED: NotificationCategory.DOCUMENT_WORKFLOW,
            DOCUMENT_PROCESSING_FAILED: NotificationCategory.DOCUMENT_WORKFLOW,
            DOCUMENT_PROCESSING_REJECTED: NotificationCategory.DOCUMENT_WORKFLOW,
            DOCUMENT_PROCESSING_STALLED: NotificationCategory.DOCUMENT_WORKFLOW,
            DOCUMENT_REVIEW_REQUEST: NotificationCategory.DOCUMENT_WORKFLOW,
            DOCUMENT_APPROVAL_REQUEST: NotificationCategory.DOCUMENT_WORKFLOW,
            DOCUMENT_APPROVED: NotificationCategory.DOCUMENT_WORKFLOW,
            DOCUMENT_REJECTED: NotificationCategory.DOCUMENT_WORKFLOW,
            DOCUMENT_VERSION_UPDATED: NotificationCategory.DOCUMENT_WORKFLOW,
            DOCUMENT_COMMENT_ADDED: NotificationCategory.DOCUMENT_WORKFLOW,
            DOCUMENT_SHARED: NotificationCategory.DOCUMENT_WORKFLOW,

            // Samples
            SAMPLE_REQUEST_CREATED: NotificationCategory.SAMPLE_MANAGEMENT,
            SAMPLE_COLLECTION_CONFIRMED: NotificationCategory.SAMPLE_MANAGEMENT,
            SAMPLE_READY_FOR_INSPECTION: NotificationCategory.SAMPLE_MANAGEMENT,
            LAB_TEST_STATUS_UPDATED: NotificationCategory.SAMPLE_MANAGEMENT,
            TEST_RESULT_AVAILABLE: NotificationCategory.SAMPLE_MANAGEMENT,
            OUT_OF_SPEC_ALERT: NotificationCategory.SAMPLE_MANAGEMENT,
            SAMPLE_INSPECTION_COMPLETED: NotificationCategory.SAMPLE_MANAGEMENT,

            // Deviations
            DEVIATION_DETECTED: NotificationCategory.DEVIATION_COMPLIANCE,
            QUALITY_EXCEPTION_ESCALATED: NotificationCategory.DEVIATION_COMPLIANCE,
            COMPLIANCE_VIOLATION_DETECTED: NotificationCategory.DEVIATION_COMPLIANCE,
            DEVIATION_INVESTIGATION_STARTED: NotificationCategory.DEVIATION_COMPLIANCE,
            DEVIATION_RESOLVED: NotificationCategory.DEVIATION_COMPLIANCE,
            REGULATORY_REPORTING_REQUIRED: NotificationCategory.DEVIATION_COMPLIANCE,

            // Collaboration
            MENTION: NotificationCategory.COLLABORATION,
            DISCUSSION_THREAD_UPDATED: NotificationCategory.COLLABORATION,
            COMMENT_REPLY: NotificationCategory.COLLABORATION,
            COMMENT_REACTION: NotificationCategory.COLLABORATION,
            TEAM_ASSIGNMENT_CHANGED: NotificationCategory.COLLABORATION,
            COLLABORATION_INVITE: NotificationCategory.COLLABORATION,

            // System
            SYSTEM_ANNOUNCEMENT: NotificationCategory.SYSTEM,
            MAINTENANCE_SCHEDULED: NotificationCategory.SYSTEM,
            SYSTEM_UPDATE_AVAILABLE: NotificationCategory.SYSTEM,
        };

        return typeToCategory[type] || NotificationCategory.SYSTEM;
    }

    /**
     * Manually refresh unread count from server via WebSocket
     */
    refreshUnreadCount(): void {
        this.wsService.requestUnreadCount();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }
}
